import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useWorkshopMode } from '../stores/WorkshopModeContext';
import { useAuth } from '../stores/AuthContext';

const WORKSHOP_MODE_PASSWORD = 'hiddenDino';

const LandingPage = () => {
  const navigate = useNavigate();

  // Single source of truth: WorkshopModeProvider (mounted in index.jsx,
  // above BrowserRouter — an ancestor of this page even though it's routed
  // outside AppProviders).
  const { workshopMode, toggleWorkshopMode } = useWorkshopMode();

  // Gates the floating "Project" button below: hide it entirely for a user
  // who isn't allowed into /projects (ProtectedRoute would just bounce them
  // straight back to /), rather than showing a dead-end button.
  const { canAccessProjects } = useAuth();

  const [clicks, setClicks] = useState(0);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const timer = useRef(null);

  const handleLogoClick = () => {
    const next = clicks + 1;
    clearTimeout(timer.current);
    if (next >= 3) {
      setClicks(0);
      setPassword('');
      setError(false);
      setPasswordDialogOpen(true);
      return;
    }
    setClicks(next);
    timer.current = setTimeout(() => setClicks(0), 1500);
  };

  const handleDialogOpenChange = (open) => {
    setPasswordDialogOpen(open);
    if (!open) {
      setPassword('');
      setError(false);
    }
  };

  const handleConfirm = () => {
    if (password === WORKSHOP_MODE_PASSWORD) {
      toggleWorkshopMode();
      setPasswordDialogOpen(false);
      setPassword('');
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center gap-10 px-6 py-10"
      style={{ background: 'linear-gradient(135deg, #2C65E8 0%, #41CCAB 100%)' }}
    >
      <div className="flex-1 flex flex-col items-center justify-center gap-12 w-full translate-y-[120px]">
        <div className="w-full max-w-5xl grid grid-cols-[1fr_auto_1fr] items-center gap-x-8">
          <img
            src="/shape_Logo_final_Designversionen_000_transparent.png"
            alt="Shape Process Intelligence"
            className="w-full h-auto justify-self-end -translate-x-[80px] cursor-pointer select-none"
            onClick={handleLogoClick}
          />
          <img
            src="/x_separator.svg"
            alt=""
            className="h-36 justify-self-center shrink-0"
          />
          <img
            src="/Ethen_Logo.svg"
            alt="Ethen"
            className="h-[165.888px] justify-self-start translate-x-[80px]"
          />
        </div>

        <img
          src="/Ethen Process Intelligence.png"
          alt="Ethen Process Intelligence"
          className="h-[187.2px] mt-[50px] -translate-y-[40px]"
        />

        <img
          src="/ProcessIntelligencefuerdenMittelstand.png"
          alt="Process Intelligence für den Mittelstand"
          className="w-full max-w-[55.86rem] h-auto"
        />

        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white text-base font-medium rounded-lg transition-colors -translate-y-[30px]"
        >
          Dashboard
        </button>
      </div>

      {!workshopMode && canAccessProjects && (
        <button
          onClick={() => navigate('/projects')}
          className="fixed bottom-5 left-5 z-50 flex items-center gap-2 px-3.5 py-2.5 bg-white border border-gray-200 shadow-lg rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:shadow-xl transition-all"
          title="Switch project"
        >
          <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
          </svg>
          Project
        </button>
      )}

      {/* Same visibility rule as the Project button above: hidden in workshop
          mode, because language is a one-off setup choice we make for the
          customer, not something they self-serve. Note this page uses
          useWorkshopMode() directly rather than <HideInWorkshop> — that
          helper lives under components/common and pulls in HideInWorkshop's
          own tree assumptions; a plain hook check is simpler here.
          This page's own strings are not migrated yet, which is why it is
          deliberately absent from the ESLint register. */}
      {!workshopMode && (
        <LanguageSwitcher className="fixed bottom-5 right-5 z-50" />
      )}

      <Dialog open={passwordDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter Password</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-2">
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Password"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">Incorrect password.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
